/**
 * HM-441 cluster 23 Manutenzione Guasti: pagina /manutenzione con lista
 * filtrabile per stato (open/resolved) e camera, conteggio aperti, drawer
 * dettaglio per editare nota / risolvere.
 *
 * Backend: routes/maintenance.ts (HM-433..436).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  type IssueStatus,
  type MaintenanceIssue,
  createIssue,
  listIssues,
} from '../../api/maintenance.js';
import { type RoomDb, fetchRooms } from '../../api/rooms.js';
import { IssueDetailDrawer } from './IssueDetailDrawer.js';

function formatItalianDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()} ${hh}:${min}`;
}

export function ManutenzionePage() {
  const [status, setStatus] = useState<IssueStatus | 'all'>('open');
  const [roomFilter, setRoomFilter] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createRoomId, setCreateRoomId] = useState('');
  const [createNote, setCreateNote] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const roomsQuery = useQuery({
    queryKey: ['rooms', 'all'],
    queryFn: fetchRooms,
    staleTime: 5 * 60_000,
  });

  const listQuery = useQuery({
    queryKey: ['maintenance', 'list', status, roomFilter],
    queryFn: () =>
      listIssues({
        status: status === 'all' ? undefined : status,
        roomId: roomFilter || undefined,
        limit: 200,
      }),
    staleTime: 15_000,
  });

  const createMutation = useMutation({
    mutationFn: createIssue,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      setShowCreateForm(false);
      setCreateRoomId('');
      setCreateNote('');
      setCreateError(null);
    },
    onError: (err: Error) => setCreateError(err.message ?? 'Errore creazione guasto'),
  });

  const rooms: RoomDb[] = roomsQuery.data?.rooms ?? [];
  const roomById = useMemo(() => {
    const m = new Map<string, RoomDb>();
    for (const r of rooms) m.set(r.id, r);
    return m;
  }, [rooms]);

  const items: MaintenanceIssue[] = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;

  function handleCreate() {
    setCreateError(null);
    if (!createRoomId) {
      setCreateError('Seleziona una camera');
      return;
    }
    const trimmed = createNote.trim();
    if (trimmed.length < 1) {
      setCreateError('Inserisci una descrizione');
      return;
    }
    createMutation.mutate({ roomId: createRoomId, note: trimmed });
  }

  return (
    <div className="manutenzione-root" data-testid="manutenzione-page" style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 600, color: '#3D2F24', margin: 0 }}>
            Manutenzione
          </h1>
          <p style={{ fontSize: 13, color: '#7A6957', margin: '4px 0 0 0' }}>
            {total} {total === 1 ? 'guasto' : 'guasti'}{' '}
            {status === 'all' ? 'in archivio' : status === 'open' ? 'aperti' : 'risolti'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateForm((v) => !v)}
          data-testid="manutenzione-toggle-create"
          style={{
            padding: '10px 16px',
            background: showCreateForm ? '#7A6957' : '#E97B3D',
            color: '#FFF',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {showCreateForm ? 'Annulla' : '+ Nuovo guasto'}
        </button>
      </div>

      {showCreateForm && (
        <div
          data-testid="manutenzione-create-form"
          style={{
            border: '1px solid #E8DECF',
            borderRadius: 8,
            background: '#FFF',
            padding: 16,
            marginBottom: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}>Camera</span>
            <select
              value={createRoomId}
              onChange={(e) => setCreateRoomId(e.target.value)}
              data-testid="manutenzione-create-room"
              style={inputStyle}
            >
              <option value="">— Seleziona —</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.code} ({r.building} · {r.floor})
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}>Descrizione</span>
            <textarea
              value={createNote}
              onChange={(e) => setCreateNote(e.target.value)}
              data-testid="manutenzione-create-note"
              rows={3}
              maxLength={2000}
              placeholder="Es. doccia non scarica, condizionatore fa rumore strano…"
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </label>
          {createError && (
            <div data-testid="manutenzione-create-error" style={{ color: '#A8453A', fontSize: 13 }}>
              {createError}
            </div>
          )}
          <div>
            <button
              type="button"
              onClick={handleCreate}
              disabled={createMutation.isPending}
              data-testid="manutenzione-create-submit"
              style={{
                padding: '10px 20px',
                background: '#E97B3D',
                color: '#FFF',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: createMutation.isPending ? 'not-allowed' : 'pointer',
                opacity: createMutation.isPending ? 0.6 : 1,
              }}
            >
              {createMutation.isPending ? 'Salvataggio…' : 'Crea guasto'}
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 12,
          marginBottom: 16,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <div role="tablist" aria-label="Filtro stato" style={{ display: 'flex', gap: 4 }}>
          {(
            [
              { id: 'open', label: 'Aperti' },
              { id: 'resolved', label: 'Risolti' },
              { id: 'all', label: 'Tutti' },
            ] as const
          ).map((tab) => {
            const active = status === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setStatus(tab.id)}
                data-testid={`manutenzione-tab-${tab.id}`}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: '1px solid',
                  borderColor: active ? '#E97B3D' : '#D4C4B0',
                  background: active ? '#FFF3E6' : '#FFF',
                  color: active ? '#C4661F' : '#3D2F24',
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  cursor: 'pointer',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <select
          value={roomFilter}
          onChange={(e) => setRoomFilter(e.target.value)}
          data-testid="manutenzione-filter-room"
          style={{ ...inputStyle, maxWidth: 240 }}
        >
          <option value="">Tutte le camere</option>
          {rooms.map((r) => (
            <option key={r.id} value={r.id}>
              {r.code}
            </option>
          ))}
        </select>
      </div>

      <div
        style={{
          border: '1px solid #E8DECF',
          borderRadius: 8,
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead style={{ background: '#FAF7F2' }}>
            <tr>
              <th style={th}>Camera</th>
              <th style={th}>Descrizione</th>
              <th style={th}>Aperto il</th>
              <th style={th}>Stato</th>
              <th style={th}>Risolto il</th>
            </tr>
          </thead>
          <tbody>
            {listQuery.isLoading && (
              <tr>
                <td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#7A6957' }}>
                  Caricamento…
                </td>
              </tr>
            )}
            {!listQuery.isLoading && items.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  style={{ padding: 24, textAlign: 'center', color: '#7A6957' }}
                  data-testid="manutenzione-empty"
                >
                  Nessun guasto da mostrare.
                </td>
              </tr>
            )}
            {items.map((it) => {
              const room = roomById.get(it.roomId);
              return (
                <tr
                  key={it.id}
                  style={{ borderTop: '1px solid #F0E8DA', cursor: 'pointer' }}
                  onClick={() => setSelectedId(it.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedId(it.id);
                    }
                  }}
                  tabIndex={0}
                  data-testid={`manutenzione-row-${it.id}`}
                >
                  <td style={td}>
                    <strong>{room?.code ?? '—'}</strong>
                    {room && (
                      <span style={{ color: '#7A6957', fontSize: 12, marginLeft: 6 }}>
                        {room.building} · {room.floor}
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, maxWidth: 420 }}>
                    <div
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {it.note}
                    </div>
                  </td>
                  <td style={td}>{formatItalianDateTime(it.openedAt)}</td>
                  <td style={td}>
                    <span
                      data-testid={`manutenzione-status-${it.id}`}
                      style={{
                        display: 'inline-block',
                        padding: '2px 10px',
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 600,
                        background: it.status === 'open' ? '#FFE5DC' : '#E4EDDE',
                        color: it.status === 'open' ? '#A8453A' : '#4A6B3A',
                      }}
                    >
                      {it.status === 'open' ? 'Aperto' : 'Risolto'}
                    </span>
                  </td>
                  <td style={td}>{formatItalianDateTime(it.resolvedAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedId && (
        <IssueDetailDrawer issueId={selectedId} rooms={rooms} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '12px 14px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: '#7A6957',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const td: React.CSSProperties = {
  padding: '12px 14px',
  verticalAlign: 'middle',
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#7A6957',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid #D4C4B0',
  fontSize: 14,
  color: '#3D2F24',
  background: '#FFF',
};
